import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HelpIcon from '@mui/icons-material/Help';
import PlayCircleFilledWhiteOutlinedIcon from '@mui/icons-material/PlayCircleFilledWhiteOutlined';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { Fragment, type ReactElement } from 'react';
import { TableBreakdownLine } from '@/features/shared/components/tableSystem/TableBreakdownLine';
import { TableBreakdownStack } from '@/features/shared/components/tableSystem/TableMetaCells';
import { resolveSemanticColor } from '@/theme/semanticTokens';
import { ErrorOutline } from '@/ui';
import { SummaryTooltipContent } from '@/ui/composites/table/cells/summaryCellUtils';
import { getSummaryTooltipItems } from '@/ui/composites/table/tableUtils';
import { Box, SuitesIcon, TestsIcon, Tooltip } from '@/ui/primitives';
import type { TestSummaryItem } from '@/ui/composites/table/tableTypes';
import type { SvgIconComponent } from '@mui/icons-material';

const BREAKDOWN_TOOLTIP_ENTER_DELAY_MS = 250;

type BreakdownStatus = 'passed' | 'failed' | 'errored' | 'skipped' | 'untested';
type SkippedIconVariant = 'filled' | 'outlined';

type TableStatusBreakdownCellProps = {
  entity: 'suite' | 'test' | 'execution';
  totalIconEntity?: 'suite' | 'test' | 'execution';
  counts: {
    passed: number;
    failed: number;
    errored: number;
    skipped: number;
    untested: number;
  };
  summaryItems?: TestSummaryItem[];
  summaryUnitLabel?: 'suite' | 'test' | 'execution';
  totalLinkMode?: 'detail' | 'execution';
  showSummaryTimestamps?: boolean;
  showSummaryDateHeaders?: boolean;
  disableSummaryLinks?: boolean;
};

const formatPercent = (count: number, total: number): string => {
  if (total <= 0) return '0%';
  return `${Math.round((count / total) * 100)}%`;
};

type StatusIconComponent = SvgIconComponent;

const getBreakdownStatusIconComponent = (
  status: BreakdownStatus,
  skippedIconVariant: SkippedIconVariant,
): StatusIconComponent => {
  if (status === 'untested') return HelpIcon;
  if (status === 'passed') return CheckCircleOutlineIcon;
  if (status === 'failed') return CancelOutlinedIcon;
  if (status === 'errored') return ErrorOutline;
  return skippedIconVariant === 'outlined' ? BlockOutlinedIcon : RemoveCircleIcon;
};

const getEntityIconComponent = (
  entity: TableStatusBreakdownCellProps['entity'],
): StatusIconComponent =>
  entity === 'suite'
    ? SuitesIcon
    : entity === 'test'
      ? TestsIcon
      : PlayCircleFilledWhiteOutlinedIcon;

const getStatusIconColor = (
  status: BreakdownStatus,
): ((theme: Parameters<typeof resolveSemanticColor>[0]) => string) => {
  if (status === 'passed') {
    return (theme: Parameters<typeof resolveSemanticColor>[0]) =>
      resolveSemanticColor(theme, 'success');
  }
  if (status === 'failed') {
    return (theme: Parameters<typeof resolveSemanticColor>[0]) =>
      resolveSemanticColor(theme, 'error');
  }
  if (status === 'errored') {
    return (theme: Parameters<typeof resolveSemanticColor>[0]) =>
      resolveSemanticColor(theme, 'brown');
  }
  if (status === 'skipped') {
    return (theme: Parameters<typeof resolveSemanticColor>[0]) =>
      resolveSemanticColor(theme, 'warning');
  }
  return (theme: Parameters<typeof resolveSemanticColor>[0]) =>
    resolveSemanticColor(theme, 'textSecondary');
};

export const TableStatusBreakdownCell = ({
  entity,
  totalIconEntity,
  counts,
  summaryItems,
  summaryUnitLabel,
  totalLinkMode = 'detail',
  showSummaryTimestamps = true,
  showSummaryDateHeaders = entity === 'execution',
  disableSummaryLinks = false,
}: TableStatusBreakdownCellProps): ReactElement => {
  const total = counts.passed + counts.failed + counts.errored + counts.skipped + counts.untested;
  const entityLabel =
    entity === 'suite'
      ? total === 1
        ? 'suite'
        : 'suites'
      : entity === 'test'
        ? total === 1
          ? 'test'
          : 'tests'
        : total === 1
          ? 'run'
          : 'runs';
  const EntityIcon = getEntityIconComponent(totalIconEntity ?? entity);
  const rows = [
    {
      key: 'passed',
      count: counts.passed,
      label: `${counts.passed.toLocaleString()} passed (${formatPercent(counts.passed, total)})`,
    },
    {
      key: 'failed',
      count: counts.failed,
      label: `${counts.failed.toLocaleString()} failed (${formatPercent(counts.failed, total)})`,
    },
    {
      key: 'errored',
      count: counts.errored,
      label: `${counts.errored.toLocaleString()} errored (${formatPercent(counts.errored, total)})`,
    },
    {
      key: 'skipped',
      count: counts.skipped,
      label: `${counts.skipped.toLocaleString()} skipped (${formatPercent(counts.skipped, total)})`,
    },
    {
      key: 'untested',
      count: counts.untested,
      label: `${counts.untested.toLocaleString()} untested (${formatPercent(counts.untested, total)})`,
    },
  ] satisfies Array<{ key: BreakdownStatus; count: number; label: string }>;
  const visibleRows = rows.filter((row) => row.count > 0);
  const resolvedSummaryUnitLabel =
    summaryUnitLabel ?? (entity === 'suite' || entity === 'test' ? entity : null);
  const skippedIconVariant: SkippedIconVariant = 'outlined';
  const allSummaryItems =
    resolvedSummaryUnitLabel === null
      ? []
      : getSummaryTooltipItems(summaryItems, { unitLabel: resolvedSummaryUnitLabel });

  const renderTooltipRow = ({
    icon,
    content,
    items,
    linkMode,
  }: {
    icon: ReactElement;
    content: string;
    items: ReturnType<typeof getSummaryTooltipItems>;
    linkMode: 'detail' | 'execution';
  }): ReactElement => (
    <Tooltip
      title={
        <SummaryTooltipContent
          items={items}
          linkMode={linkMode}
          skippedIconVariant={skippedIconVariant}
          showDetailLabel={entity !== 'execution'}
          showTimestamp={showSummaryTimestamps}
          showDateHeaders={showSummaryDateHeaders}
          disableLinks={disableSummaryLinks}
        />
      }
      enterDelay={BREAKDOWN_TOOLTIP_ENTER_DELAY_MS}
      enterNextDelay={BREAKDOWN_TOOLTIP_ENTER_DELAY_MS}
      disableHoverListener={items.length === 0}
      arrow
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: 'none',
            width: 'fit-content',
            fontSize: '15px',
            lineHeight: 1.4,
          },
        },
      }}
    >
      <Box
        component="span"
        sx={{ display: 'inline-block', width: 'fit-content', maxWidth: '100%' }}
      >
        <TableBreakdownLine
          icon={icon}
          content={content}
          ariaLabel={items.length === 0 ? content : undefined}
        />
      </Box>
    </Tooltip>
  );

  return (
    <TableBreakdownStack>
      {renderTooltipRow({
        icon: (
          <EntityIcon
            sx={{
              fontSize: (theme) => theme.app.iconSizes.sm,
              flexShrink: 0,
              color: (theme) => resolveSemanticColor(theme, 'textSecondary'),
              transform: 'translateY(2px)',
            }}
          />
        ),
        content: `${total.toLocaleString()} ${entityLabel}`,
        items: allSummaryItems,
        linkMode: totalLinkMode,
      })}
      {visibleRows.map((row) => {
        const StatusIcon = getBreakdownStatusIconComponent(row.key, skippedIconVariant);
        const statusIconColor = getStatusIconColor(row.key);
        const statusSummaryItems =
          resolvedSummaryUnitLabel === null
            ? []
            : getSummaryTooltipItems(summaryItems, {
                unitLabel: resolvedSummaryUnitLabel,
                status: row.key,
              });
        return (
          <Fragment key={row.key}>
            {renderTooltipRow({
              icon: (
                <StatusIcon
                  sx={{
                    fontSize: (theme) => theme.app.iconSizes.sm,
                    flexShrink: 0,
                    color: statusIconColor,
                    transform: 'translateY(2px)',
                  }}
                />
              ),
              content: row.label,
              items: statusSummaryItems,
              linkMode: 'execution',
            })}
          </Fragment>
        );
      })}
    </TableBreakdownStack>
  );
};
